import { useEffect, useMemo, useState } from "react";

import CompanyAPI from "../company/CompanyAPI";
import HallAppToast from "../../commons/HallAppToast";
import NotificationAPI from "./notifications/NotificationAPI";
import Skeleton from "../../commons/skeleton/Skeleton";
import { requestNotificationPermissionAndToken } from "../../commons/firebase/message";

const companyAPI = new CompanyAPI();
const notificationAPI = new NotificationAPI();

function formatDateTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  } catch {
    return iso;
  }
}

function ToggleSpinner() {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 14,
        height: 14,
        borderRadius: "50%",
        border: "2px solid rgba(0,0,0,.18)",
        borderTopColor: "rgba(0,0,0,.55)",
        display: "inline-block",
        animation: "hallappSpin .8s linear infinite",
      }}
    />
  );
}

function NotificationCardSkeleton({ count = 4 }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10 }}>
      {Array.from({ length: count }).map((_, idx) => (
        <div
          // eslint-disable-next-line react/no-array-index-key
          key={idx}
          style={{
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            padding: 14,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 12,
          }}
        >
          {/* esquerda */}
          <div style={{ minWidth: 0, width: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <Skeleton variant="text" width="55%" height={14} />
              <Skeleton variant="rect" width={44} height={18} style={{ borderRadius: 999 }} />
            </div>

            <div style={{ display: "grid", gap: 6 }}>
              <Skeleton variant="text" width="92%" height={12} />
              <Skeleton variant="text" width="86%" height={12} />
              <Skeleton variant="text" width="62%" height={12} />
            </div>

            <div style={{ marginTop: 10 }}>
              <Skeleton variant="text" width="40%" height={11} />
            </div>
          </div>

          {/* direita (ações) */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: "0 0 auto" }}>
            <Skeleton variant="rect" width={88} height={32} style={{ borderRadius: 10 }} />
            <Skeleton variant="rect" width={88} height={32} style={{ borderRadius: 10 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function NotificationsSettings() {
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(true);
  const [token, setToken] = useState(null);

  const [loadingList, setLoadingList] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const unreadCount = useMemo(
    () => (notifications || []).filter((n) => !n.read).length,
    [notifications]
  );

  useEffect(() => {

    //setEnabled(e);
    //setToken(t || null);
    getNotifications();
    getEnabledNotify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function getEnabledNotify() {
    setBusy(true);
    try {
      const resp = await notificationAPI.getEnabledNotify();
      setEnabled(resp.data || false);
    } catch (e) {
      HallAppToast.genericError();
    } finally {
      setBusy(false);
    }
  }

  async function getNotifications() {
    setLoadingList(true);
    try {
      const resp = await notificationAPI.getNotifications();
      setNotifications(resp.data || []);
    } catch (e) {
      HallAppToast.genericError();
    } finally {
      setLoadingList(false);
    }
  }

  async function enableNotifications() {
    setBusy(true);
    try {
      const { token, permission } = await requestNotificationPermissionAndToken("BFlf90Q2dmeKJPXmCAZ1Je0yHG5qN5XejTIYkNkwk2B19azTZJ7Ud3_2fC8-QS-VOnEsZKhBpaMj7Y1lToBoshc");

      if (permission !== "granted") {
        HallAppToast.error("Permissão negada. Ative nas configurações do navegador.");
        setEnabled(false);
        return;
      }

      if (!token) {
        HallAppToast.error("Não foi possível gerar o token de notificação.");
        setEnabled(false);
        return;
      }

      const data = {
        token,
        companyId: companyAPI.getCompanyManagment().id,
        enabled: true,
        platform: "WEB"
      };

      await notificationAPI.saveConnectionToFirebase(data);

      setToken(token);
      setEnabled(true);

      HallAppToast.success("Notificações ativadas!");
    } catch (e) {
      HallAppToast.genericError();
      setEnabled(false);
    } finally {
      setBusy(false);
    }
  }

  async function disableNotifications() {
    setBusy(true);
    try {

      const data = {
        companyId: companyAPI.getCompanyManagment().id,
        enabled: false,
        platform: "WEB"
      };

      await notificationAPI.saveConnectionToFirebase(data);

      setToken(null);
      setEnabled(false);

      HallAppToast.success("Notificações desativadas (no app).");
    } catch (e) {
      HallAppToast.genericError();
    } finally {
      setBusy(false);
    }
  }

  async function handleToggle() {
    if (busy) return;
    if (!enabled) return enableNotifications();
    return disableNotifications();
  }

  async function markAsRead(notificationId) {
    try {
      setLoadingList(true);
      await notificationAPI.markAsRead(notificationId);
      await getNotifications();
    } catch (e) {
      HallAppToast.genericError();
    } finally {
      setLoadingList(false);
    }
  }

  return (
    <div style={{
      padding: 16,
      paddingBottom: `calc(16px + var(--bottom-nav-h, 64px) + env(safe-area-inset-bottom))`,
      maxWidth: 920,
      margin: "0 auto",
    }}>
      {/* spinner animation local */}
      <style>
        {`
          @keyframes hallappSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        `}
      </style>

      {/* Header */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
          <div>
            <h5 style={{ marginBottom: 6 }}>Notificações</h5>
            <p style={{ marginTop: 0, color: "#6b7280", marginBottom: 0 }}>
              Ative para receber alertas de agendamentos.
            </p>
          </div>

          <div
            style={{
              fontSize: 12,
              color: unreadCount > 0 ? "#111827" : "#6b7280",
              background: "#f3f4f6",
              borderRadius: 999,
              padding: "6px 10px",
              whiteSpace: "nowrap",
            }}
          >
            {unreadCount > 0 ? `${unreadCount} não lidas` : "Tudo em dia"}
          </div>
        </div>
      </div>

      {/* Card: Toggle */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: 14,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          marginBottom: 14,
        }}
      >
        <div>
          <div style={{ fontWeight: 600 }}>Ativar notificações</div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>Você pode desligar no app quando quiser.</div>
          {token && <div style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}>Token conectado ✅</div>}
        </div>

        <button
          type="button"
          onClick={handleToggle}
          disabled={busy}
          style={{
            width: 54,
            height: 32,
            borderRadius: 999,
            border: "none",
            background: enabled ? "#16a34a" : "#e5e7eb",
            position: "relative",
            cursor: busy ? "not-allowed" : "pointer",
            transition: "all .2s",
            padding: 0,
            flex: "0 0 auto",
            opacity: busy ? 0.85 : 1,
          }}
          aria-pressed={enabled}
          aria-label={enabled ? "Desativar notificações" : "Ativar notificações"}
        >
          <span
            style={{
              width: 26,
              height: 26,
              borderRadius: "50%",
              background: "#fff",
              position: "absolute",
              top: 3,
              left: enabled ? 25 : 3,
              transition: "all .2s",
              boxShadow: "0 1px 4px rgba(0,0,0,.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {busy ? <ToggleSpinner /> : null}
          </span>
        </button>
      </div>

      {/* Header lista + refresh */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ fontWeight: 600 }}>Últimas notificações</div>

        <button
          type="button"
          onClick={getNotifications}
          disabled={loadingList}
          style={{
            border: "1px solid #e5e7eb",
            background: "#fff",
            borderRadius: 10,
            padding: "8px 10px",
            fontSize: 13,
            cursor: loadingList ? "not-allowed" : "pointer",
            color: "#111827",
          }}
        >
          {loadingList ? "Atualizando..." : "Atualizar"}
        </button>
      </div>

      {/* Lista */}
      {loadingList ? (
        <NotificationCardSkeleton count={4} />
      ) : notifications.length === 0 ? (
        <div
          style={{
            background: "#fff",
            border: "1px dashed #e5e7eb",
            borderRadius: 12,
            padding: 16,
            color: "#6b7280",
            fontSize: 13,
          }}
        >
          Nenhuma notificação por enquanto.
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10 }}>
          {notifications.map((n) => (
            <div
              key={n.id}
              style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                padding: 14,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 12,
                opacity: n.read ? 0.72 : 1,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <div style={{ fontWeight: 700, color: "#111827", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {n.title}
                  </div>

                  {!n.read && (
                    <span
                      style={{
                        fontSize: 11,
                        background: "#111827",
                        color: "#fff",
                        padding: "2px 8px",
                        borderRadius: 999,
                        flex: "0 0 auto",
                      }}
                    >
                      nova
                    </span>
                  )}
                </div>

                <div style={{ color: "#374151", fontSize: 13, lineHeight: 1.35 }}>{n.message}</div>

                <div style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}>
                  {formatDateTime(n.createDate)}
                  {n.readAt ? ` • lida em ${formatDateTime(n.readAt)}` : ""}
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: "0 0 auto" }}>
                {!n.read && (
                  <button
                    type="button"
                    onClick={() => markAsRead(n.id)}
                    disabled={loadingList}
                    style={{
                      border: "1px solid #e5e7eb",
                      background: "#fff",
                      borderRadius: 10,
                      padding: "8px 10px",
                      fontSize: 12,
                      cursor: loadingList ? "not-allowed" : "pointer",
                      whiteSpace: "nowrap",
                      opacity: loadingList ? 0.7 : 1,
                    }}
                  >
                    Marcar lida
                  </button>
                )}

                {!!n.referenceId && n.referenceType === "SCHEDULING" && (
                  <a
                    href="/#/agenda"
                    style={{
                      border: "1px solid #e5e7eb",
                      background: "#f9fafb",
                      borderRadius: 10,
                      padding: "8px 10px",
                      fontSize: 12,
                      textDecoration: "none",
                      color: "#111827",
                      textAlign: "center",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Ver agenda
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ height: 16 }} />
    </div>
  );
}
